import React from 'react';
import { useState } from 'react';
import { Copy, Check } from 'lucide-react';
import Tooltip from '../common_jsx/Tooltip';

function CopyButton(props) {
    const [isCopied, setIsCopied] = useState(false);

    // Determine if copy should be disabled based on selected tab
    const isDisabled = props.selectedTab === 'data' || props.selectedTab === 'chart';

    // Helper function to extract human-readable text from content
    const getHumanReadableText = (content) => {
        if (typeof content === 'string') {
            return content;
        }
        if (Array.isArray(content)) {
            return content.map(item => {
                if (typeof item === 'object' && item !== null) {
                    // For SQL source with component_id and query
                    if (item.query) return item.query;
                    if (item.curl) return item.curl;
                    return JSON.stringify(item);
                }
                return String(item);
            }).join('\n\n');
        }
        if (typeof content === 'object' && content !== null) {
            if (content.query) return content.query;
            return JSON.stringify(content, null, 2);
        }
        return String(content || '');
    };

    // onClick handler function for the copy button
    const handleCopyClick = () => {
        if (isDisabled) return; // Prevent action if disabled

        let textToBeCopied = '';
        
        if (props.selectedTab === 'description') {
            // For direct content (RAG) - copy raw data_sources values
            if (props.dataSourcesMap && typeof props.dataSourcesMap === 'object') {
                textToBeCopied = Object.values(props.dataSourcesMap)
                    .filter(val => val && typeof val === 'string')
                    .join('\n\n');
            }
            // For Insights tab - copy description if available, otherwise message
            if (!textToBeCopied && props.description) {
                textToBeCopied = props.description;
            }
            if (!textToBeCopied) {
                textToBeCopied = getHumanReadableText(props.copyText);
            }
        } else if (props.selectedTab === 'source') {
            // For Source tab - copy the source content (SQL and/or curl commands)
            const parts = [];
            
            // Add SQL source if available
            const sourceText = getHumanReadableText(props.source);
            if (sourceText && sourceText.trim() !== '' && sourceText !== 'null' && sourceText !== 'undefined') {
                parts.push(sourceText);
            }
            
            // Add curl commands if available
            if (props.curl_commands && props.curl_commands.length > 0) {
                const curlTexts = props.curl_commands.map(cmd => {
                    const curlContent = cmd.curl || '';
                    return cmd.url ? `URL: ${cmd.url}\n${curlContent}` : curlContent;
                }).filter(text => text.trim() !== '');
                
                if (curlTexts.length > 0) {
                    parts.push(curlTexts.join('\n\n'));
                }
            }
            
            // If we have content, join it; otherwise show fallback message
            if (parts.length > 0) {
                textToBeCopied = parts.join('\n\n');
            } else {
                textToBeCopied = 'No Source to display.';
            }
        }
        
        // Asynchronously call copyTextToClipboard
        navigator.clipboard.writeText(textToBeCopied)
            .then(() => {
                // If successful, update the isCopied state value
                setIsCopied(true);
                setTimeout(() => {
                    setIsCopied(false);
                }, 1500);
            })
            .catch((err) => {
                console.error(err);
            });
    }

    return (
        <Tooltip text={isDisabled ? "Copy disabled" : (isCopied ? "Copied!" : "Copy")}>
            <button
                className={`p-2 rounded-lg transition-colors ${
                    isDisabled
                        ? 'text-fg-muted opacity-50 cursor-not-allowed'
                        : 'text-fg-muted hover:text-fg hover:bg-surface-raised cursor-pointer'
                }`}
                onClick={handleCopyClick}
                disabled={isDisabled}
            >
                {isCopied ? (
                    <Check className="w-4 h-4 text-success" />
                ) : (
                    <Copy className="w-4 h-4" />
                )}
            </button>
        </Tooltip>
    );
}
export default CopyButton;
